import { useState, useMemo, useEffect } from 'react';
import { Typography, Grid2 as Grid, TextField, debounce } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CommonCard } from '../../component/cards/MainCard2';
import { SelectionButton, EditButton } from '../../component/buttons';
import { useUsers } from '../../hooks/useUsers';
import ManagementHeader from './componets/ManagementHeader';
import UsersActionsCell from './componets/UsersActionsCell';

const ROLES = [
    { key: 'PATIENT', label: 'Patients' },
    { key: 'DOCTOR', label: 'Doctors' },
    { key: 'CLINIC_ADMIN', label: 'Admins' }
];

const ClinicUsersManagement = () => {
    const { state } = useLocation();

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    const clinic = state?.clinic;

    const [selectedRole, setSelectedRole] = useState(0);

    const columns = useMemo(
        () => [
            {
                field: 'name',
                headerName: t('clinics.name'),
                flex: 1
            },
            {
                field: 'email',
                headerName: t('clinics.email'),
                flex: 1
            },
            {
                field: 'last_access',
                headerName: t('clinics.lastAcess'),
                flex: 1,
                renderCell: (params) => {
                    const value = params?.row?.last_access;

                    if (!value) return '-';

                    const date = new Date(value);

                    return date.toLocaleString('pt-PT', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                }
            },
            {
                field: 'actions',
                headerName: t('clinics.actions'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                sortable: false,
                filterable: false,
                renderCell: (params) =>
                    params?.row ? (
                        <UsersActionsCell user={params.row} clinic={clinic} />
                    ) : null
            }
        ],
        [t]
    );

    useEffect(() => {
        setPaginationModel((prev) => ({
            ...prev,
            page: 0
        }));
    }, [search]);

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                setSearch(value);
            }, 1500),
        []
    );

    const {
        data: users,
        isLoading,
        rowsPerPageOptions,
        count,
        paginationModel,
        setPaginationModel
    } = useUsers([ROLES[selectedRole].key], search, clinic?.uuid);

    return (
        <CommonCard sx={{ m: 2, p: 2 }}>
            {/* Header */}
            <ManagementHeader
                title={clinic ? t('clinics.users') : t('users.allUsers')}
                subtitle={clinic?.name}
                backTo="/mgmt/clinics"
                actions={
                    <EditButton
                        onClick={() =>
                            navigate('/mgmt/clinic/users/create', {
                                state: {
                                    clinic,
                                    preselectedRole: ROLES[selectedRole].key
                                }
                            })
                        }
                    >
                        <Typography variant="subtitle2">
                            {t('users.addNewUser')}
                        </Typography>
                    </EditButton>
                }
            />

            {/* Role selector */}
            <Grid container spacing={2} mb={3}>
                {ROLES.map((role, index) => (
                    <Grid key={role.key} size={{ xs: 4 }}>
                        <SelectionButton
                            selected={selectedRole === index}
                            sx={{ width: '100%', py: 2 }}
                            onClick={() => setSelectedRole(index)}
                        >
                            {t(`clinics.${role.key}`)}
                        </SelectionButton>
                    </Grid>
                ))}
            </Grid>

            {/* Search User */}
            <TextField
                label={t('users.searchUser')}
                value={searchInput}
                onChange={(e) => {
                    setSearchInput(e.target.value);
                    debouncedSearch(e.target.value);
                }}
                fullWidth
                sx={{ mb: 2 }}
            />

            {/* Users table */}
            <DataGrid
                autoHeight
                rows={users}
                getRowId={(row) => row.role_uuid}
                columns={columns}
                loading={isLoading}
                rowCount={count}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={rowsPerPageOptions}
            />
        </CommonCard>
    );
};

export default ClinicUsersManagement;
